import React from 'react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Ratings} from '@/components/ratings'
import Price from '@/components/product-tile-cms/price'
import AddToCart from '../add-to-cart'
import {useProductContext} from '@/context/ProductContext'
import {ProductDataType, VariationAttributeType} from '@/types/product'
import {ProductVariantPicker} from './productVariantPicker'
import {qualifiesForFreeShipping} from './utils'
import {cn} from '@/lib/utils'
import BundledProduct from '../product-tile-cms/bundledProduct'
import slugify from 'slugify'

export interface ProductDetailProps {
    className?: string
}

const ProductDetail: React.FC<ProductDetailProps> = ({className}) => {
    const [showAllBundled, setShowAllBundled] = React.useState(false)
    const siteId: string = getConfig().app.commerceAPI.parameters.siteId

    const {product, activeProduct, setVariantProductId, bundledProducts} = useProductContext()

    const [initializedVariant, setInitializedVariant] = React.useState(false)

    React.useEffect(() => {
        if (
            product &&
            product.variation_id &&
            product.variants &&
            product.variants.length > 0 &&
            !activeProduct &&
            !initializedVariant
        ) {
            const defaultVariant = product.variants.find(
                (v) => v.productId === product.variation_id
            )
            if (defaultVariant && setVariantProductId) {
                setVariantProductId(defaultVariant.productId)
                setInitializedVariant(true)
            }
        }
    }, [product, activeProduct, setVariantProductId, initializedVariant])

    if (!product) return null

    const {
        brand,
        c_brandURL,
        price,
        url,
        rating,
        number_of_reviews,
        name,
        c_siteShortDescription,
        variationAttributes = [],
        variants = [],
        id,
        type
    }: ProductDataType = product

    const isFreeShipping = !type?.master
        ? qualifiesForFreeShipping(price)
        : activeProduct?.price && qualifiesForFreeShipping(activeProduct.price)

    return (
        <div className={cn('body-common', className)}>
            <a
                href={url}
                className="text-primary font-grotesque mb-[1rem] min-h-[2.9rem] text-xl font-medium"
            >
                {activeProduct?.name ? activeProduct.name : name}
            </a>
            {brand && (
                <div className="flex mt-[1rem]">
                    <p>
                        Shop More{' '}
                        <a
                            href={
                                c_brandURL ||
                                `/search?q=${slugify(brand, {
                                    lower: true,
                                    strict: true,
                                    replacement: '+'
                                })}`
                            }
                            className="underline text-primary"
                        >
                            {brand}
                        </a>
                    </p>
                </div>
            )}
            <div className="flex mt-[1rem]">
                <Ratings
                    rating={rating}
                    reviewCount={number_of_reviews}
                    siteId={siteId}
                    size="large"
                    data-testid="productDetail-Ratings"
                />
            </div>
            <div>
                <Price
                    className="font-grotesque font-medium text-xl mt-[1rem]"
                    price={activeProduct?.price ? activeProduct.price : price}
                    siteId={siteId}
                    data-testid="productDetail-Price"
                />
            </div>
            {isFreeShipping && (
                <div className="flex mt-[1rem] text-accent text-sm">This item ships free!</div>
            )}
            {type?.master && (
                <div className="flex mt-[1rem]">
                    {variationAttributes.map((attribute: VariationAttributeType, index: number) => {
                        // Determine selected value for this attribute
                        let selectedValue = activeProduct?.variationValues?.[attribute.id]
                        if (!selectedValue && product.variation_id) {
                            const defaultVariant = variants.find(
                                (v) => v.productId === product.variation_id
                            )
                            selectedValue = defaultVariant?.variationValues?.[attribute.id]
                        }
                        return (
                            <ProductVariantPicker
                                key={`product-variant-picker-${id}-${index}`}
                                variationAttribute={attribute}
                                variants={variants}
                                selectedValue={selectedValue}
                                data-testid="productDetail-ProductVariantPicker"
                            />
                        )
                    })}
                </div>
            )}
            <div className="flex mt-[1rem]">
                <AddToCart
                    showStockStatus={true}
                    isProductTile={true}
                    data-testid="productDetail-AddToCart"
                />
            </div>
            {c_siteShortDescription && (
                <div className="mt-[1rem]">
                    <p
                        className="text-primary"
                        dangerouslySetInnerHTML={{__html: c_siteShortDescription}}
                    ></p>
                </div>
            )}
            <div className="mt-[1rem]">
                <a href={url} className="font-medium underline">
                    View Full Details
                </a>
            </div>
            {/* for loop over bundledProducts that renders a BundledProduct component for each item */}
            {bundledProducts && bundledProducts.length > 0 && (
                <div className="mt-[1rem]">
                    <div className="flex flex-col space-y-2 p-3 text-foreground font-medium">
                        Items in this bundle:
                    </div>
                    <div className="flex flex-col space-y-2">
                        {(showAllBundled ? bundledProducts : bundledProducts.slice(0, 3)).map(
                            (product: ProductDataType, index: number) => (
                                <BundledProduct
                                    key={`bundled-product-${index}`}
                                    bundledProduct={product}
                                />
                            )
                        )}
                    </div>
                    {bundledProducts.length > 3 && !showAllBundled && (
                        <button
                            className="text-left font-grotesque pointer-events-auto box-border text-[#142745] cursor-pointer text-sm font-medium mb-4 underline leading-6"
                            onClick={() => setShowAllBundled(true)}
                        >
                            Show More Products
                        </button>
                    )}
                    {bundledProducts.length > 3 && showAllBundled && (
                        <button
                            className="text-left font-grotesque pointer-events-auto box-border text-[#142745] cursor-pointer text-sm font-medium mb-4 underline leading-6"
                            onClick={() => setShowAllBundled(false)}
                        >
                            Show Less Products
                        </button>
                    )}
                </div>
            )}
        </div>
    )
}

export {ProductDetail}
